import { useCallback } from 'react';
import * as Cesium from 'cesium';
import { buildMapThemeProvider } from '../utils/mapThemeProvider';
import { createDualDataSource } from '../state/dataSourceManager';
import type { MapTheme } from '../types';

export function usePrimaryViewer(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  secondarySharedDataSourceRef: React.MutableRefObject<any>,
  previewDataSourceRef: React.MutableRefObject<any>,
  secondaryPreviewDataSourceRef: React.MutableRefObject<any>,
  highlightDataSourceRef: React.MutableRefObject<any>,
  secondaryHighlightDataSourceRef: React.MutableRefObject<any>,
  selectionDataSourceRef: React.MutableRefObject<any>,
  secondarySelectionDataSourceRef: React.MutableRefObject<any>,
  measureDataSourceRef: React.MutableRefObject<any>,
  secondaryMeasureDataSourceRef: React.MutableRefObject<any>,
  setCursorLatLon: (coords: { lat: number; lon: number } | null) => void,
  gsActiveMap: React.MutableRefObject<Record<string, boolean>>,
  geoPointImagePropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  highlightDataSourceRef2: React.MutableRefObject<any>,
  pickCartesian: (v: Cesium.Viewer, pos: Cesium.Cartesian2) => Cesium.Cartesian3 | undefined,
) {
  const initViewer = useCallback((el: HTMLDivElement) => {
    if (viewerRef.current) return;

    const viewer = new Cesium.Viewer(el, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      animation: false,
      timeline: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      shadows: false,
      shouldAnimate: false,
      orderIndependentTranslucency: false,
      maximumRenderTimeChange: 0.0,
      targetFrameRate: 60,
      contextOptions: {
        webgl: {
          alpha: false,
          depth: true,
          stencil: false,
          antialias: false,
          powerPreference: 'high-performance',
        },
        allowTextureFilterAnisotropic: false,
      },
    });

    viewer.useBrowserRecommendedResolution = false;
    viewer.resolutionScale = 1.0;
    viewer.scene.globe.depthTestAgainstTerrain = true;
    viewer.scene.globe.enableLighting = false;
    viewer.scene.globe.maximumScreenSpaceError = 2.0;
    viewer.scene.globe.tileCacheSize = 100;
    viewer.scene.globe.loadingDescendantLimit = 2;
    viewer.scene.screenSpaceCameraController.enableCollisionDetection = false;
    viewer.camera.percentageChanged = 0.002;
    if (viewer.scene.postProcessStages?.fxaa) {
      viewer.scene.postProcessStages.fxaa.enabled = true;
    }

    const hide = (sel: string) => {
      const el2 = (viewer.container as HTMLElement).querySelector(sel) as HTMLElement | null;
      if (el2) el2.style.display = 'none';
    };
    ['cesium-viewer-bottom', '.cesium-viewer-toolbar',
      '.cesium-viewer-animationContainer', '.cesium-viewer-timelineContainer',
      '.cesium-viewer-fullscreenContainer'].forEach(hide);

    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(71, 21, 22_000_000),
      duration: 0,
    });

    viewerRef.current = viewer;

    viewer.scene.preRender.addEventListener(() => {
      (window as any).currentRenderingViewer = viewer;
    });

    void buildMapThemeProvider('dark').then((provider) => {
      if (!viewerRef.current || viewerRef.current.isDestroyed()) return;
      viewerRef.current.imageryLayers.removeAll();
      viewerRef.current.imageryLayers.addImageryProvider(provider);
      viewerRef.current.scene.requestRender();
    });

    const missionDS = new Cesium.CustomDataSource('mission');
    sharedDataSourceRef.current = createDualDataSource(missionDS, () => secondarySharedDataSourceRef.current);
    viewer.dataSources.add(missionDS);

    const previewDS = new Cesium.CustomDataSource('sensorPreview');
    previewDataSourceRef.current = createDualDataSource(previewDS, () => secondaryPreviewDataSourceRef.current);
    viewer.dataSources.add(previewDS);

    const highlightDS = new Cesium.CustomDataSource('highlight');
    highlightDataSourceRef.current = createDualDataSource(highlightDS, () => secondaryHighlightDataSourceRef.current);
    viewer.dataSources.add(highlightDS);

    const selectionDS = new Cesium.CustomDataSource('selection');
    selectionDataSourceRef.current = createDualDataSource(selectionDS, () => secondarySelectionDataSourceRef.current);
    viewer.dataSources.add(selectionDS);

    const measureDS = new Cesium.CustomDataSource('measure');
    measureDataSourceRef.current = createDualDataSource(measureDS, () => secondaryMeasureDataSourceRef.current);
    viewer.dataSources.add(measureDS);

    missionDS.show = false;
    previewDS.show = false;
    highlightDS.show = false;
    selectionDS.show = false;
    measureDS.show = false;

    let initialLoaded = false;
    const showDataSources = () => {
      if (initialLoaded) return;
      initialLoaded = true;
      missionDS.show = true;
      previewDS.show = true;
      highlightDS.show = true;
      selectionDS.show = true;
      measureDS.show = true;
      try { removeProgress(); } catch { /* ignore */ }
    };

    const fallbackTimeout = setTimeout(showDataSources, 1500);

    const removeProgress = viewer.scene.globe.tileLoadProgressEvent.addEventListener((queuedTileCount) => {
      if (queuedTileCount === 0) {
        showDataSources();
        clearTimeout(fallbackTimeout);
      }
    });

    let hoveredGsId: string | null = null;
    let hoveredGeoPointId: string | null = null;

    const cursorHandler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    cursorHandler.setInputAction((e: { endPosition: Cesium.Cartesian2 }) => {
      if (viewer.isDestroyed()) return;

      const cart = pickCartesian(viewer, e.endPosition);
      if (cart) {
        const c = Cesium.Cartographic.fromCartesian(cart);
        if (c) {
          const newLat = parseFloat(Cesium.Math.toDegrees(c.latitude).toFixed(4));
          const newLon = parseFloat(Cesium.Math.toDegrees(c.longitude).toFixed(4));
          if (!hoveredGsId && (!hoveredGeoPointId)) {
            setCursorLatLon({ lat: newLat, lon: newLon });
          }
        }
      } else {
        setCursorLatLon(null);
      }

        const picked = viewer.scene.pick(e.endPosition);
        const pickedId = picked?.id?.toString() ?? '';
        const pickedGsId = pickedId.startsWith('gs_point_') ? pickedId.replace('gs_point_', '') : null;
        const pickedGeoPointId = pickedId && geoPointImagePropsRef.current.has(pickedId) ? pickedId : null;

        if (pickedGsId && pickedGsId !== hoveredGsId) {
          if (hoveredGsId) {
            gsActiveMap.current[`${hoveredGsId}_hover`] = false;
            const hoverEntity = highlightDataSourceRef2.current?.entities.getById('gs_hover_marker');
            if (hoverEntity) highlightDataSourceRef2.current?.entities.remove(hoverEntity);
          }
          hoveredGsId = pickedGsId;
          if (hoveredGsId) {
            gsActiveMap.current[`${hoveredGsId}_hover`] = true;
            const gsEntity = sharedDataSourceRef.current?.entities.getById(`gs_point_${hoveredGsId}`);
            if (gsEntity?.position) {
              const hds = highlightDataSourceRef2.current;
              const hoverEntity = hds?.entities.getById('gs_hover_marker');
              if (hoverEntity) hds?.entities.remove(hoverEntity);
              hds?.entities.add({
                id: 'gs_hover_marker',
                position: gsEntity.position,
                billboard: {
                  image: '/assets/gs_hover.svg',
                  scale: 1.0,
                  verticalOrigin: Cesium.VerticalOrigin.CENTER,
                  disableDepthTestDistance: Number.POSITIVE_INFINITY,
                },
              });
            }
          }
        } else if (!pickedGsId && hoveredGsId) {
          gsActiveMap.current[`${hoveredGsId}_hover`] = false;
          const hoverEntity = highlightDataSourceRef2.current?.entities.getById('gs_hover_marker');
          if (hoverEntity) highlightDataSourceRef2.current?.entities.remove(hoverEntity);
          hoveredGsId = null;
        }

        if (pickedGeoPointId && pickedGeoPointId !== hoveredGeoPointId) {
          if (hoveredGeoPointId) {
            const imageProp = geoPointImagePropsRef.current.get(hoveredGeoPointId);
            if (imageProp) imageProp.setValue('/assets/point_white.png');
          }
          hoveredGeoPointId = pickedGeoPointId;
          if (hoveredGeoPointId) {
            const imageProp = geoPointImagePropsRef.current.get(hoveredGeoPointId);
            if (imageProp) imageProp.setValue('/assets/point_white_hover.png');
          }
        } else if (!pickedGeoPointId && hoveredGeoPointId) {
          const imageProp = geoPointImagePropsRef.current.get(hoveredGeoPointId);
          if (imageProp) imageProp.setValue('/assets/point_white.png');
          hoveredGeoPointId = null;
        }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    const nowJD = Cesium.JulianDate.now();
    const icrfInterval = new Cesium.TimeInterval({
      start: Cesium.JulianDate.addDays(nowJD, -1, new Cesium.JulianDate()),
      stop: Cesium.JulianDate.addDays(nowJD, 2, new Cesium.JulianDate()),
    });
    Cesium.Transforms.preloadIcrfFixed(icrfInterval).catch(() => { });

    viewer.scene.morphComplete.addEventListener(() => {
      viewer.resize();
    });

    return viewer;
  }, [pickCartesian]);

  return {
    initViewer,
  };
}
